import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DeleteButton from '../components/DeleteButton';
import { LocationTreeModal, useLocations } from '../components/LocationTree';
import MoveBar from '../components/MoveBar';
import './ItemsList.css';

function ItemsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const locations = useLocations();
  const [moving, setMoving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [destination, setDestination] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch('/api/items/all');
      const data = await response.json();
      setItems(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching items:', error);
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.locationPath && item.locationPath.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const allVisibleSelected = filteredItems.length > 0 && filteredItems.every(item => selectedIds.has(item.id));

  const startMove = () => {
    setSelectedIds(new Set());
    setDestination(null);
    setMoveError('');
    setMoving(true);
  };

  const cancelMove = () => {
    setMoving(false);
    setPickerOpen(false);
    setMoveError('');
  };

  const toggleItem = (itemId) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      filteredItems.forEach(item => (allVisibleSelected ? next.delete(item.id) : next.add(item.id)));
      return next;
    });

  const doMove = async () => {
    setMoveBusy(true);
    setMoveError('');
    try {
      const response = await fetch('/api/move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemIds: [...selectedIds], destinationId: destination.id || null })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setMoveError(data.error || `Move failed (${response.status})`);
      } else {
        cancelMove();
        await fetchItems();
      }
    } catch (error) {
      setMoveError('Could not reach the server. If you were signed out, reload the page and sign in again.');
    }
    setMoveBusy(false);
  };

  if (loading) {
    return <div className="items-page"><p>Loading items...</p></div>;
  }

  return (
    <div className={`items-page${moving ? ' moving' : ''}`}>
      <div className="page-header">
        <h1>Items</h1>
        <div className="page-header-actions">
          {items.length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={startMove} disabled={moving}>Move</button>
          )}
          <Link to="/items/new" className="btn btn-primary">Add Item</Link>
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search items by name, description, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredItems.length === 0 ? (
        <div className="no-items">
          <p>No items found. Create one to get started!</p>
          <Link to="/items/new" className="btn btn-primary">Create First Item</Link>
        </div>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                {moving && <th />}
                <th>Name</th>
                <th>Description</th>
                <th>Count</th>
                <th>Location</th>
                {!moving && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => (
                <tr key={item.id}>
                  {moving && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleItem(item.id)}
                        aria-label={`Select ${item.name}`}
                      />
                    </td>
                  )}
                  <td className="item-name"><Link to={`/items/${item.id}`}>{item.name}</Link></td>
                  <td className="item-desc">{item.description || '-'}</td>
                  <td className="item-count">{item.count}</td>
                  <td className="item-location">
                    {item.locationPath ? (
                      <span className="location-path">{item.locationPath}</span>
                    ) : (
                      <span className="no-location">Unassigned</span>
                    )}
                  </td>
                  {!moving && (
                    <td className="item-actions">
                      <DeleteButton
                        url={`/api/items/${item.id}`}
                        noun="item"
                        className="btn btn-small btn-danger"
                        onDeleted={fetchItems}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {moving && (
        <MoveBar
          count={selectedIds.size}
          allSelected={allVisibleSelected}
          onToggleAll={toggleAll}
          destinationLabel={destination ? destination.path : null}
          onChooseDestination={() => setPickerOpen(true)}
          onMove={doMove}
          onCancel={cancelMove}
          busy={moveBusy}
          error={moveError}
        />
      )}
      {pickerOpen && (
        <LocationTreeModal
          title="Move to..."
          topLabel="No location (unassigned)"
          locations={locations}
          selectedId={destination ? destination.id : ''}
          onSelect={(destId, path) => {
            setDestination({ id: destId, path });
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default ItemsList;
